import { throwError, of, Observable, BehaviorSubject } from 'rxjs';

import { first, map, switchMap } from 'rxjs/operators';
import { Injectable, Predicate } from '@angular/core';
import { Person } from '../models/index';
import * as _ from 'lodash';
import { ApiService } from './api.service';
import { ApiMode } from '../../utils/enums';
import { OfflineApiService } from './offlineApi.service';
import { BaseService } from './baseService';
import { AppConstants } from '../../utils/appConstants.class';
import { ConnectionCheck } from '../../utils/connection-check';

@Injectable({
  providedIn: 'root'
})
export class PeopleDataService extends BaseService {
  constructor(private apiService: ApiService, private offlineApi: OfflineApiService) {
    super();
  }

  private currentPerson: Person;
  private people: Person[];
  private authorizationBehavior = new BehaviorSubject<any>(null);
  private languageBehavior = new BehaviorSubject<any>(null);
  private permissions: any = [];

  public getPerson(id: string): Observable<Person> {
    const url = '/people/' + id;
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.people, id).pipe(
      map((res: Person) => {
        return res;
      })
    );
  }

  public getCurrent(): Observable<Person> {
    if (this.currentPerson != null) {
      return of(this.currentPerson);
    }

    const url = '/people/current';
    const currentPersonId = localStorage.getItem(AppConstants.CURRENT_USER);
    return ConnectionCheck.isConnection$.pipe(
      first(),
      switchMap((isConnection) =>
        this.offlineApi.getWithMode(
          url,
          isConnection ? ApiMode.OnlineSave : ApiMode.Offline,
          AppConstants.INDEXEDDB_TABLES.people,
          currentPersonId
        )
      ),
      map((res: Person) => {
        this.currentPerson = Object.assign(new Person(), res);
        this.authorizationBehavior.next(res);
        this.languageBehavior.next(res.language);
        localStorage.setItem(AppConstants.CURRENT_USER, res.id);
        localStorage.setItem(AppConstants.CURRENT_USER_NAME, res.username);
        return res;
      })
    );
  }

  public setCurrent(person: Person) {
    this.currentPerson = person;
  }

  public getPeople(): Observable<Person[]> {
    return this._getPeople().pipe(
      map((people: Person[]) => {
        this.processPeoples(people);
        return people;
      })
    );
  }

  public getFilteredPeople(predicate: Predicate<Person>) {
    return this.getPeople().map((people) => people.filter((p) => predicate(p)));
  }

  public getCurrentScopePeople(): Observable<Person[]> {
    if (this.currentPerson.localEntities.includes('localentity-*')) {
      return this.getPeople();
    }
    return this.getFilteredPeople(
      this.some([
        (p) =>
          p.localEntities.some((localEntity) => {
            return this.currentPerson.localEntities.includes(localEntity);
          }),
        (p) => p.localEntities.includes('localentity-*')
      ])
    );
  }

  private _getPeople(): Observable<Person[]> {
    const url = '/people/list';
    const table = 'people';
    if (this.isManager) {
      return this.apiService.get(url).pipe(map((data) => data.Items));
    }
    const lastSyncDate = localStorage.getItem(
      `${table}${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`
    );
    return this.offlineApi.postSyncList(url, lastSyncDate, table);
  }

  public processPeoples(people: Person[]) {
    for (let i = 0; i < people.length; i++) {
      const person = people[i];
      people[i] = this.processPerson(person);
    }
  }

  public processPerson(person: Person) {
    if (!('search' in person)) {
      return _.merge(new Person(), person);
    }
  }

  public addPerson(params: any): Observable<Person> {
    const url = '/people';
    return this.apiService.post(url, params).pipe(
      map((res: Person) => {
        return res;
      })
    );
  }

  public updatePerson(id: string, params: any): Observable<Person> {
    const url = '/people/' + id;
    return this.apiService.patch(url, params).pipe(
      map((res: Person) => {
        return res;
      })
    );
  }

  public managePerson(id: string, params: any): Observable<Person> {
    const url = '/people/manage/' + id;
    return this.apiService.patch(url, params).pipe(
      map((res: Person) => {
        this.processPerson(res);
        return res;
      })
    );
  }

  public updatePeople(params: any): Observable<Person[]> {
    const url = '/people/bulkupdate';
    return this.apiService.post(url, params).pipe(
      map((res: Person[]) => {
        res.forEach((person) => {
          this.processPerson(person);
        });

        return res;
      })
    );
  }

  public deletePerson(id: string): any {
    const url = '/people/manage/' + id;
    return this.apiService.delete(url).pipe(
      map((success: boolean) => {
        if (success) {
        }
        return success;
      })
    );
  }

  public deletePeople(peopleToDelete: string[]): any {
    const url = '/people/manage/bulkdelete';
    return this.apiService.post(url, peopleToDelete).pipe(
      map((success: boolean) => {
        if (success) {
        }
        return success;
      })
    );
  }

  public updateCurrentPerson(id: string, params: any): Observable<Person> {
    return this.updatePerson(id, params).pipe(
      map((person: Person) => {
        this.currentPerson = person;
        return person;
      })
    );
  }

  public getPermissions(): Observable<any> {
    return this.authorizationBehavior.asObservable();
  }

  public getPersonLanguage(): Observable<string> {
    return this.languageBehavior.asObservable();
  }

  public getPersonEntity(personId: string): Observable<Person> {
    const url = `/people/${personId}/entity`;
    return this.apiService.get(url);
  }

  public updatePersonEntity(personId: string, person: any): Observable<Person> {
    const url = `/people/${personId}/entity`;
    return this.apiService.put(url, person);
  }
}
